import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Badge,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Stack,
  Text,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  VStack
} from '@chakra-ui/react';
import {
  FaArrowLeft,
  FaBriefcase,
  FaChevronRight,
  FaEnvelope,
  FaIdCard,
  FaMapMarkerAlt,
  FaMapPin,
  FaPhone,
  FaSearch,
  FaUser,
  FaWhatsapp
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../api';
import PageLayout from './PageLayout';

const WatermarkCard = ({ children, ...props }) => {
  return (
    <Box
      bg="white"
      borderRadius="22px"
      p={5}
      position="relative"
      overflow="hidden"
      boxShadow="0 12px 26px rgba(15,23,42,0.12)"
      {...props}
    >
      <Box
        position="absolute"
        inset="0"
        bgImage="url('/maconaria.png')"
        bgRepeat="no-repeat"
        bgPosition="center"
        bgSize="220px"
        opacity={0.06}
        pointerEvents="none"
      />
      <Box position="relative" zIndex={1}>
        {children}
      </Box>
    </Box>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <HStack align="start" spacing={3}>
      <Box
        width="34px"
        height="34px"
        borderRadius="12px"
        bg="rgba(147,200,230,0.35)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="blue.700"
        flexShrink={0}
      >
        <Icon />
      </Box>
      <Box>
        <Text fontSize="xs" color="gray.500">{label}</Text>
        <Text fontWeight="medium" color="gray.800">{value}</Text>
      </Box>
    </HStack>
  );
};

const ContactLine = ({ label, value }) => {
  if (!value) return null;
  return (
    <Flex
      align="center"
      justify="space-between"
      bg="gray.50"
      borderRadius="16px"
      px={4}
      py={3}
    >
      <Text fontSize="sm" color="gray.500">{label}</Text>
      <Text fontWeight="medium" color="gray.800">{value}</Text>
    </Flex>
  );
};

const toDigits = (value) => (value ? value.replace(/\D/g, '') : '');

const buildWhatsappLink = (value) => {
  const digits = toDigits(value);
  if (!digits) return '';
  const withCountry = digits.startsWith('55') ? digits : `55${digits}`;
  return `https://wa.me/${withCountry}`;
};

const buildMapsLink = (item) => {
  if (!item) return '';
  const address = [item.endereco, item.numero, item.complemento, item.cidade]
    .filter(Boolean)
    .join(' ');
  if (!address) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

const normalizeText = (value) => {
  if (!value) return '';
  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

const QuadroDeObreio = () => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [familiaData, setFamiliaData] = useState([]);
  const [profissaoData, setProfissaoData] = useState([]);
  const [profissoesPorId, setProfissoesPorId] = useState({});
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const navigate = useNavigate();
  const streetLine = selectedItem
    ? [selectedItem.endereco, selectedItem.numero, selectedItem.complemento]
        .filter(Boolean)
        .join(', ')
    : '';

  const getProfissaoLabel = (item) => {
    const profRegistro = profissoesPorId[item.id_pessoa] || {};
    return (
      profRegistro.profissao ||
      profRegistro.ramo ||
      profRegistro.cargo ||
      profRegistro.funcao ||
      item.profissao ||
      item.ramo ||
      item.cargo ||
      item.cidade ||
      'Cadastro ativo'
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [membrosResp, profissoesResp] = await Promise.all([
          axios.get(`${API_BASE}/getMembros?id_pessoa=0&id_insituicao=0`),
          axios.get(`${API_BASE}/getProfissaoPessoa?id_pessoa=0`)
        ]);
        setData(membrosResp.data.result[0]);
        const profissoes = profissoesResp.data.result[0] || [];
        setProfissoesPorId(
          profissoes.reduce((acc, prof) => {
            if (prof?.id_pessoa) {
              acc[prof.id_pessoa] = prof;
            }
            return acc;
          }, {})
        );
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
    };

    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    const query = normalizeText(searchTerm);
    if (!query) return data;
    return data.filter((item) => {
      const profRegistro = profissoesPorId[item.id_pessoa] || {};
      const baseValues = [
        item.nome,
        item.profissao,
        item.ramo,
        item.cidade,
        item.email,
        item.cargo,
        item.funcao,
        profRegistro.profissao,
        profRegistro.ramo,
        profRegistro.cargo,
        profRegistro.funcao,
        profRegistro.atividade
      ];
      const extraValues = Object.entries(item || {})
        .filter(([key]) => /prof|ramo|ocup|trabalh|cargo|func/i.test(key))
        .map(([, value]) => value);
      const values = [...baseValues, ...extraValues];
      return values.some((value) => normalizeText(value).includes(query));
    });
  }, [data, searchTerm, profissoesPorId]);

  const handleItemClick = async (item) => {
    setSelectedItem(item);
    await fetchFamiliaData(item.id_pessoa);
    await fetchProfissaoData(item.id_pessoa);
    onOpen();
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const fetchFamiliaData = async (idPessoa) => {
    try {
      const response = await axios.get(`${API_BASE}/getFamiliaPessoa?id_pessoa=${idPessoa}`);
      setFamiliaData(response.data.result[0]);
    } catch (error) {
      toast({
        title: 'Erro ao buscar dados da familia.',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const fetchProfissaoData = async (idPessoa) => {
    try {
      const response = await axios.get(`${API_BASE}/getProfissaoPessoa?id_pessoa=${idPessoa}`);
      setProfissaoData(response.data.result[0]);
    } catch (error) {
      toast({
        title: 'Erro ao buscar dados de profissao.',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  return (
    <PageLayout
      title="Quadro de Obreiros"
      subtitle="Membros"
      onBack={handleHomeClick}
      accent="#93C8E6"
      background="#EEF3F7"
      watermark="/maconaria.png"
    >
      <Stack spacing={5}>
        <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
          <Box
            flex="1"
            bg="white"
            borderRadius="20px"
            p={4}
            boxShadow="0 12px 30px rgba(15,23,42,0.22)"
          >
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FaSearch color="#94A3B8" />
              </InputLeftElement>
              <Input
                placeholder="Buscar por nome ou profiss?o"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                border="none"
                bg="gray.50"
                borderRadius="14px"
                color="gray.800"
                _placeholder={{ color: 'gray.400' }}
              />
            </InputGroup>
          </Box>
          <Box
            bg="white"
            borderRadius="20px"
            p={4}
            boxShadow="0 12px 30px rgba(15,23,42,0.22)"
            minW={{ base: '100%', md: '220px' }}
          >
            <Text fontSize="sm" color="gray.500">Total</Text>
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">{filteredData.length}</Text>
            <Badge colorScheme="blue" variant="subtle" mt={2}>Membros ativos</Badge>
          </Box>
        </Flex>

        {filteredData.length === 0 ? (
          <Center bg="white" borderRadius="24px" p={8} boxShadow="0 12px 30px rgba(15,23,42,0.22)">
            <Text color="gray.500">Nenhum responsavel encontrado.</Text>
          </Center>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
            {filteredData.map((responsavel) => {
              const professionLabel = getProfissaoLabel(responsavel);
              return (
                <Box
                  key={responsavel.id_pessoa}
                  p={4}
                bg="white"
                borderRadius="22px"
                boxShadow="0 14px 30px rgba(15,23,42,0.22)"
                cursor="pointer"
                onClick={() => handleItemClick(responsavel)}
                transition="all 0.2s ease"
                _hover={{ transform: 'translateY(-4px)', boxShadow: '0 20px 40px rgba(15,23,42,0.25)' }}
              >
                <Flex align="center" justify="space-between">
                  <HStack spacing={4}>
                    <Box
                      bg="rgba(147,200,230,0.5)"
                      borderRadius="18px"
                      p={2}
                    >
                      <Image
                        src={`${API_BASE}/Fotos/${responsavel.id_pessoa}.png`}
                        alt={responsavel.nome}
                        boxSize="64px"
                        borderRadius="16px"
                        objectFit="cover"
                      />
                    </Box>
                    <VStack align="start" spacing={0} minW="0">
                      <Text fontWeight="bold" color="gray.800" noOfLines={1}>{responsavel.nome}</Text>
                      <Text fontSize="sm" color="gray.500" noOfLines={1}>{professionLabel}</Text>
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {responsavel.ramo || responsavel.cargo || '—'}
                      </Text>
                      {(responsavel.cidade || responsavel.estado || responsavel.uf) && (
                        <Text fontSize="xs" color="gray.400" noOfLines={1}>
                          {responsavel.cidade || 'Cidade não informada'}
                          {responsavel.estado || responsavel.uf ? ` / ${responsavel.estado || responsavel.uf}` : ''}
                        </Text>
                      )}
                    </VStack>
                  </HStack>
                  <Button size="sm" rightIcon={<FaChevronRight />} variant="ghost" color="gray.400">
                    Ver
                  </Button>
                </Flex>
              </Box>
            );
            })}
          </SimpleGrid>
        )}
      </Stack>

      {selectedItem && (
        <Modal isOpen={isOpen} onClose={onClose} size="full">
          <ModalOverlay />
          <ModalContent bg="#EEF3F7">
            <ModalHeader
              display="flex"
              alignItems="center"
              gap={3}
              color="gray.700"
              px={{ base: 4, md: 6 }}
              py={{ base: 4, md: 5 }}
            >
              <IconButton
                aria-label="Voltar"
                icon={<FaArrowLeft />}
                onClick={onClose}
                size="sm"
                variant="ghost"
                color="gray.600"
                _hover={{ bg: 'gray.100' }}
              />
              <Text fontSize="lg" fontWeight="bold" color="gray.700">
                Detalhes do contato
              </Text>
            </ModalHeader>
            <ModalBody pb={10} position="relative">
              <Box
                position="absolute"
                inset="0"
                bgImage="url('/maconaria.png')"
                bgRepeat="no-repeat"
                bgPosition="center"
                bgSize="420px"
                opacity={0.04}
                pointerEvents="none"
              />
              <Stack spacing={6} maxW="720px" mx="auto" position="relative" zIndex={1}>
                <WatermarkCard borderRadius="26px" p={{ base: 5, md: 6 }} boxShadow="0 14px 30px rgba(15,23,42,0.12)">
                  <Stack spacing={4} align="center" textAlign="center">
                    <Box
                      bg="rgba(147,200,230,0.5)"
                      borderRadius="999px"
                      p={3}
                    >
                      <Image
                        src={`${API_BASE}/Fotos/${selectedItem.id_pessoa}.png`}
                        alt={selectedItem.nome}
                        boxSize="110px"
                        borderRadius="full"
                        objectFit="cover"
                      />
                    </Box>
                    <Box>
                      <Text fontSize="2xl" fontWeight="bold" color="gray.800">{selectedItem.nome}</Text>
                      <Text color="gray.500">{selectedItem.profissao || 'Membro ativo'}</Text>
                    </Box>
                  </Stack>
                </WatermarkCard>

                <WatermarkCard>
                  <Text fontWeight="bold" color="gray.700" mb={4}>Contato</Text>
                  <Stack spacing={3}>
                    <ContactLine label="Telefone" value={selectedItem.fone} />
                    <ContactLine label="WhatsApp" value={selectedItem.whatsapp} />
                    <ContactLine label="Email" value={selectedItem.email} />
                  </Stack>
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mt={5}>
          <Button
            leftIcon={<FaPhone />}
            borderRadius="full"
            bg="green.50"
            color="green.700"
            isDisabled={!selectedItem.fone}
            onClick={() => {
              const digits = toDigits(selectedItem.fone);
              if (digits) {
                window.open(`tel:${digits}`, '_self');
              }
            }}
          >
            Ligar
          </Button>
          <Button
            leftIcon={<FaWhatsapp />}
            borderRadius="full"
            bg="green.100"
            color="green.800"
            isDisabled={!selectedItem.whatsapp}
            onClick={() => {
              const link = buildWhatsappLink(selectedItem.whatsapp);
              if (link) {
                window.open(link, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            WhatsApp
          </Button>
          <Button
            leftIcon={<FaEnvelope />}
            borderRadius="full"
            bg="blue.50"
            color="blue.700"
            isDisabled={!selectedItem.email}
            onClick={() => {
              if (selectedItem.email) {
                window.open(`mailto:${selectedItem.email}`, '_self');
              }
            }}
          >
            Email
          </Button>
          <Button
            leftIcon={<FaMapMarkerAlt />}
            borderRadius="full"
            bg="orange.50"
            color="orange.700"
            isDisabled={!selectedItem.endereco && !selectedItem.cidade}
            onClick={() => {
              const link = buildMapsLink(selectedItem);
              if (link) {
                window.open(link, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      Maps
                    </Button>
                  </SimpleGrid>
                </WatermarkCard>

                <WatermarkCard>
                  <Text fontWeight="bold" color="gray.700" mb={4}>Endereco</Text>
                  <Stack spacing={4}>
                    <InfoRow icon={FaMapMarkerAlt} label="Rua" value={streetLine} />
                    <InfoRow icon={FaMapPin} label="Cidade" value={selectedItem.cidade} />
                  </Stack>
                </WatermarkCard>

                {profissaoData.length > 0 && (
                  <WatermarkCard>
                    <Text fontWeight="bold" color="gray.700" mb={4}>Profissao</Text>
                    <Stack spacing={4}>
                      <InfoRow icon={FaBriefcase} label="Profissao" value={profissaoData[0].profissao} />
                      <InfoRow icon={FaIdCard} label="Ramo" value={profissaoData[0].ramo} />
                      <InfoRow icon={FaMapMarkerAlt} label="Local" value={profissaoData[0].local} />
                      <InfoRow
                        icon={FaMapPin}
                        label="Endereco"
                        value={`${profissaoData[0].endereco || ''} ${profissaoData[0].numero || ''} ${profissaoData[0].complemento || ''}`.trim()}
                      />
                      <InfoRow icon={FaWhatsapp} label="WhatsApp" value={profissaoData[0].whatsapp} />
                      <InfoRow icon={FaPhone} label="Fone" value={profissaoData[0].fone} />
                      <InfoRow icon={FaEnvelope} label="Email" value={profissaoData[0].email} />
                    </Stack>
                  </WatermarkCard>
                )}

                {familiaData.length > 0 && (
                  <WatermarkCard>
                    <Text fontWeight="bold" color="gray.700" mb={4}>Familiares</Text>
                    <Stack spacing={3} divider={<Divider borderColor="gray.200" />}>
                      {familiaData.map((familiar, index) => (
                        <Flex key={`${familiar.id_familia || index}`} align="center" justify="space-between">
                          <HStack spacing={3}>
                            <Box
                              width="34px"
                              height="34px"
                              borderRadius="12px"
                              bg="rgba(147,200,230,0.35)"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              color="blue.700"
                            >
                              <FaUser />
                            </Box>
                            <Box>
                              <Text fontWeight="medium" color="gray.800">{familiar.nome}</Text>
                              <Text fontSize="sm" color="gray.500">{familiar.tipo}</Text>
                            </Box>
                          </HStack>
                        </Flex>
                      ))}
                    </Stack>
                  </WatermarkCard>
                )}
              </Stack>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </PageLayout>
  );
};

export default QuadroDeObreio;
